import { Link } from '@tanstack/react-router'
import { DropdownMenuItem } from 'designSystem/components/DropdownMenu'
import { sessionRecordingControl } from 'features/analytics/sessionRecordingControl'
import { MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST } from 'features/manageUsers'
import { PermissionsGate } from 'features/permissions'
import React from 'react'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { createUsersSearch } from 'routes/users/-searchParams'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import {
  type ConfigSettingsProps,
  type ConfigSettingsState,
} from './useConfigSettingsState'
import { WORKSPACE_SETTINGS_DESTINATION } from './workspaceSettingsLink'

export type WorkspaceSettingsNavigateHandler = (
  event: React.MouseEvent<HTMLAnchorElement>,
  info: { label: string; destinationKey: string }
) => void

export function ConfigSettingsSidebarMenuItems({
  orgID,
  siteID,
  state,
  onWorkspaceNavigate,
}: ConfigSettingsProps & {
  state: ConfigSettingsState
  onWorkspaceNavigate: WorkspaceSettingsNavigateHandler
}) {
  const linkClick =
    (label: string, destinationKey: string) =>
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onWorkspaceNavigate(event, { label, destinationKey })
    }

  return (
    <>
      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownMenuItem asChild>
          <Link
            to='/app-config'
            onClick={linkClick(
              WORKSPACE_SETTINGS_DESTINATION.appConfig,
              WORKSPACE_SETTINGS_DESTINATION.appConfig
            )}>
            Org & Site App Config
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      {orgID && (
        <PermissionsGate requires={MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST}>
          <DropdownMenuItem asChild>
            <Link
              to='/users'
              search={createUsersSearch({ orgId: orgID, siteId: siteID })}
              onClick={linkClick(
                WORKSPACE_SETTINGS_DESTINATION.manageUsers,
                WORKSPACE_SETTINGS_DESTINATION.manageUsers
              )}>
              Manage Users
            </Link>
          </DropdownMenuItem>
        </PermissionsGate>
      )}

      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownMenuItem asChild>
          <Link
            to='/admin/users'
            onClick={linkClick(
              WORKSPACE_SETTINGS_DESTINATION.managePermissions,
              WORKSPACE_SETTINGS_DESTINATION.managePermissions
            )}>
            Manage Permissions
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownMenuItem asChild>
          <Link
            to='/admin/access'
            onClick={linkClick(
              WORKSPACE_SETTINGS_DESTINATION.manageAccess,
              WORKSPACE_SETTINGS_DESTINATION.manageAccess
            )}>
            Manage Access
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      {state.isSessionRecordingOrg && (
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault()
              state.setRecordingOn(sessionRecordingControl.toggle())
            }}>
            Session recording: {state.recordingOn ? 'On' : 'Off (this browser)'}
          </DropdownMenuItem>
        </PermissionsGate>
      )}

      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownMenuItem asChild>
          <Link
            to='/health-check'
            onClick={linkClick(
              WORKSPACE_SETTINGS_DESTINATION.healthCheck,
              WORKSPACE_SETTINGS_DESTINATION.healthCheck
            )}>
            Health Check
          </Link>
        </DropdownMenuItem>
      </PermissionsGate>

      {orgID && (
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <DropdownMenuItem asChild>
            <Link
              to={'/extensions'}
              search={(prev) => ({
                ...prev,
                [SEARCH_PARAM_ORG_ID]: orgID,
                [SEARCH_PARAM_SITE_ID]: siteID,
              })}
              onClick={linkClick(
                WORKSPACE_SETTINGS_DESTINATION.manageExtensions,
                WORKSPACE_SETTINGS_DESTINATION.manageExtensions
              )}>
              Manage Extensions
            </Link>
          </DropdownMenuItem>
        </PermissionsGate>
      )}
    </>
  )
}
